import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { InfoBox } from "../../components/InfoBox";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { StockBalanceId, StoreId } from "@KassaroAB/shared/types/ids";
import type { RestGetCostPool } from "@KassaroAB/shared/types/costPool";
import { StockLedgerEntryType } from "@KassaroAB/shared/types/enums/StockLedgerEntryType";
import { useGetCostPoolQuery } from "../../query/costPoolsQuery";
import { formatMoney } from "../../utils/formatMoney";
import { formatDate } from "../../utils/dateUtils";

type Layer = RestGetCostPool["layers"][number];

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px 0;
`;

const Bar = styled.div`
  display: flex;
  gap: 2px;
  height: 12px;
  border-radius: 6px;
  overflow: hidden;
`;

const Segment = styled.div<{ $weight: number; $opacity: number }>`
  flex: ${({ $weight }) => $weight};
  background: ${colors.primary};
  opacity: ${({ $opacity }) => $opacity};
`;

const Swatch = styled.span<{ $opacity: number }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: ${spacing.x1}px;
  border-radius: 2px;
  background: ${colors.primary};
  opacity: ${({ $opacity }) => $opacity};
`;

const LayerTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${fontSizes.small}px;

  th {
    padding: ${spacing.x05}px ${spacing.x1}px;
    color: ${colors.textMuted};
    font-weight: 600;
    text-align: left;
  }

  td {
    padding: ${spacing.x1}px;
    border-top: 1px solid ${colors.border};
  }

  .right {
    text-align: right;
    white-space: nowrap;
  }
`;

const Muted = styled.p`
  margin: 0;
  color: ${colors.textMuted};
  font-size: ${fontSizes.small}px;
`;

const opacityOf = (index: number, count: number) =>
  count <= 1 ? 1 : 1 - (index / (count - 1)) * 0.65;

export const CostLayersPanel = ({
  storeId,
  poolKey,
  skuKey,
}: {
  storeId: StoreId;
  poolKey: string;
  skuKey: StockBalanceId;
}) => {
  const { t } = useTranslation();
  const {
    data: pool,
    isLoading,
    error,
  } = useGetCostPoolQuery(storeId, { poolKey, skuKey });

  if (isLoading) {
    return <Muted>{t("stock.layers.loading")}</Muted>;
  }
  if (error || !pool) {
    return <InfoBox variant="error">{t("stock.layers.errorLoad")}</InfoBox>;
  }

  const sourceLabel = (layer: Layer) => {
    if (!layer.source) {
      return "—";
    }
    if (
      layer.source.type === StockLedgerEntryType.TRANSFER_IN &&
      layer.source.fromLocationName
    ) {
      return t("stock.layers.transferFrom", {
        location: layer.source.fromLocationName,
      });
    }
    const label = t(`stock.layers.source.${layer.source.type}`);
    return layer.source.reference
      ? `${label} · ${layer.source.reference}`
      : label;
  };
  const hasSerials = pool.layers.some((layer) => !!layer.serial);
  const [next] = pool.layers;

  return (
    <Panel>
      {pool.layers.length > 0 && (
        <Bar aria-hidden="true">
          {pool.layers.map((layer, index) => (
            <Segment
              key={index}
              $weight={layer.remaining}
              $opacity={opacityOf(index, pool.layers.length)}
            />
          ))}
        </Bar>
      )}
      {pool.layers.length > 0 ? (
        <LayerTable>
          <thead>
            <tr>
              <th scope="col">{t("stock.layers.receivedAt")}</th>
              <th scope="col">{t("stock.layers.from")}</th>
              {hasSerials ? (
                <th scope="col">{t("stock.serial")}</th>
              ) : (
                <th scope="col" className="right">
                  {t("stock.layers.remaining")}
                </th>
              )}
              <th scope="col" className="right">
                {t("stock.layers.unitCost")}
              </th>
              <th scope="col" className="right">
                {t("stock.value")}
              </th>
            </tr>
          </thead>
          <tbody>
            {pool.layers.map((layer, index) => (
              <tr key={index}>
                <td>
                  <Swatch
                    aria-hidden="true"
                    $opacity={opacityOf(index, pool.layers.length)}
                  />
                  {formatDate(layer.receivedAt)}
                </td>
                <td>{sourceLabel(layer)}</td>
                {hasSerials ? (
                  <td>
                    {layer.serial ??
                      t("stock.layers.withoutSerial", {
                        count: layer.remaining,
                      })}
                  </td>
                ) : (
                  <td className="right">
                    {layer.source && layer.source.received >= layer.remaining
                      ? t("stock.layers.remainingOf", {
                          remaining: layer.remaining,
                          received: layer.source.received,
                        })
                      : layer.remaining}
                  </td>
                )}
                <td className="right">{formatMoney(layer.unitCost)}</td>
                <td className="right">{formatMoney(layer.value)}</td>
              </tr>
            ))}
          </tbody>
        </LayerTable>
      ) : (
        !pool.deficit && <Muted>{t("stock.layers.empty")}</Muted>
      )}
      {pool.deficit && pool.deficit.quantity > 0 && (
        <InfoBox variant="warning">
          {t("stock.layers.deficit", {
            count: pool.deficit.quantity,
            cost: formatMoney(pool.deficit.provisionalCost),
          })}
        </InfoBox>
      )}
      {next && !hasSerials && (
        <Muted>
          {t("stock.layers.next", { cost: formatMoney(next.unitCost) })}
        </Muted>
      )}
      <Muted>{t("stock.layers.semiLive")}</Muted>
    </Panel>
  );
};
